import { useState } from "react";
import { HiOutlineArrowTopRightOnSquare } from "react-icons/hi2";
import useAuthedApi from "../hooks/useAuthedApi";
import BasicInput from "../design-system/input-text/BasicInput.jsx";
import PasswordInput from "../design-system/input-text/PasswordInput.jsx";
import CircleInfo from "../design-system/icons/CircleInfo.jsx";

const labelClass = "text-sm font-medium text-[#21272A] mb-1 block";

export default function CredentialsForm({ existing, suggestedLabel, onSaved }) {
  const { authedPost } = useAuthedApi();
  const [label, setLabel] = useState(existing?.label || "");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isTradingEnabled] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const res = await authedPost("/api/borrow/credentials/", {
        credential_id: existing?.id,
        label: label.trim(),
        api_key: apiKey,
        api_secret: apiSecret,
        passphrase,
        is_trading_enabled: isTradingEnabled,
        is_testnet: false,
      });
      onSaved?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/icon_okx.svg" alt="OKX" className="w-8 h-8 rounded-lg" />
          <span className="text-sm font-medium text-[#21272A]">OKX</span>
        </div>
        <a href="#" className="text-sm text-[#5B3DF6] font-medium flex items-center gap-1">
          How to Connect <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
        </a>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 text-blue-700 text-sm rounded-lg px-3 py-2">
        <CircleInfo className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          This exchange requires <span className="font-medium">"Futures mode"</span>, please check your OKX
          exchange account settings — you have to whitelist these IP addresses:{" "}
          <span className="font-medium">82.25.250.226, 82.25.250.9, 82.25.250.78</span>.
        </span>
      </div>

      {existing && (
        <div className="rounded-xl bg-[#F2F4F8] px-3 py-2 text-sm text-[#21272A]">
          Current key: <span className="font-mono">{existing.api_key_masked}</span>{" "}
          <span className="text-xs text-[#697077]">({existing.status})</span>
        </div>
      )}

      <div>
        <label className={labelClass}>Account label</label>
        <BasicInput
          placeholder={suggestedLabel || "e.g. OKX-Sub Account-1 (optional)"}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>

      <div>
        <label className={labelClass}>API Key *</label>
        <BasicInput
          placeholder="Input API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          required
        />
      </div>

      <div>
        <label className={labelClass}>Secret Key *</label>
        <PasswordInput
          placeholder="Input Secret Key"
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          showPassword={showSecret}
          onToggle={() => setShowSecret((s) => !s)}
          required
        />
      </div>

      <div>
        <label className={labelClass}>Pass Phrase</label>
        <PasswordInput
          placeholder="Input Pass Phrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          showPassword={showPassphrase}
          onToggle={() => setShowPassphrase((s) => !s)}
        />
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <button
        type="submit"
        disabled={saving}
        className="bg-[#007A78] text-white rounded-xl py-2 text-sm font-medium disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save credentials"}
      </button>
    </form>
  );
}
